/**
 * PR-F — Multileader entity → MultileaderPrimitive[] adapter.
 *
 * LEGACY `EntityPreview.properties` string-bag adapter. Decodes the
 * `MLENT_*` / `MLVAR_*` DIMVAR-style keys + the JSON-encoded leader
 * hierarchy back out of the bag and delegates the actual primitive
 * math to the SHARED builder (`../adapters/geom/multileaderGeometry.ts`) —
 * the TYPED sibling (`../adapters/typed/multileaderFromEntity.ts`, DEFUSE
 * Lane H / S9) extracts the SAME raw fields directly off the canonical
 * `MultileaderEntity` and calls the identical shared builder, so the two
 * adapters cannot diverge on the geometry math itself, only on where the
 * raw numbers come from.
 *
 * Still reachable for: the draft-preview channel
 * (`draft/draftPreviewLayer.tsx`, an in-flight MLEADER draft has no typed
 * document entity yet — though its dispatcher case currently returns
 * `null`, see `../adapters/index.ts`) and the PDF export converter
 * (`print/pdf/converters/multileaderConverter.ts`), which both still feed a
 * bag `EntityPreview`.
 *
 * # MLEADERSTYLE override flags
 *
 * Mirrors the SVG renderer's `MLEADER_OVERRIDE_*` bitmask: MLENT_* keys
 * override MLVAR_* when the matching override flag is set. Annotative
 * context overrides everything when active.
 *
 * PRE-EXISTING GAP (documented, out of Lane H scope): the render feed
 * builder (`state/dxfGeometry.ts` `buildMultileaderPreview`) never actually
 * populates the `MLVAR_*` half of the bag (only `resolveMlVarProperties`,
 * consumed by the Properties-panel translator, does) — so in the LIVE bag
 * render, `readMlNum`/`readMlStr`/`readMlBool` below always resolve to the
 * ENTITY's own value regardless of `propertyOverrideFlags`, because the
 * style-side bag key is always absent. The typed adapter reproduces this
 * (reads `entity.*` fields directly, no MLEADERSTYLE table lookup) for
 * golden parity — see `multileaderFromEntity.ts`'s module doc.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1
 *
 * No simplification: spline leaders use Catmull-Rom cubic Bezier
 * (not quadratic), every leader root + node + line is enumerated
 * (not just the first), break points split lines into sub-segments,
 * block-content goes through the same recursive flatten as a top-
 * level INSERT.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import {
  safeRecordLookup,
  type BlockDefinition,
} from "@dxf-ai-editor/drawing-model";

import { readNumber } from "../../scene/renderHelpers";
import {
  pickAnnotativeContext,
  type EncodedAnnotativeContext,
  type EncodedBlockAttrRef,
  type EncodedLeaderRoot,
} from "../../scene/renderMultileader";
import type { RenderableEntity } from "../types";

import {
  buildRenderableShell,
  resolveShellColorHex,
  type AdapterContext,
} from "../adapters/adapterContext";
import {
  buildMultileaderGeometry,
  mapBlockAttributeReferences,
  toMultileaderSceneGeometry,
  type MultileaderRawFields,
  type RawLeaderRoot,
} from "../adapters/geom/multileaderGeometry";
import { flattenBlockReference } from "../adapters/typed/blockFromEntity";

/* ── MLEADERSTYLE override flag bits (mirrors renderMultileader.tsx) ─ */

const FLAG_LEADER_TYPE = 1 << 0;
const FLAG_ENABLE_LANDING = 1 << 4;
const FLAG_LANDING_GAP = 1 << 5;
const FLAG_ENABLE_DOGLEG = 1 << 6;
const FLAG_DOGLEG_LENGTH = 1 << 7;
const FLAG_ARROWHEAD = 1 << 8;
const FLAG_ARROWHEAD_SIZE = 1 << 9;
const FLAG_CONTENT_TYPE = 1 << 10;
const FLAG_TEXT_COLOR = 1 << 15;
const FLAG_TEXT_HEIGHT = 1 << 16;
const FLAG_BLOCK_CONTENT_BLOCK = 1 << 19;
const FLAG_BLOCK_CONTENT_COLOR = 1 << 20;
const FLAG_BLOCK_CONTENT_SCALE = 1 << 21;
const FLAG_BLOCK_CONTENT_ROTATION = 1 << 22;

function readMlNum(
  props: Record<string, string>,
  flagOn: boolean,
  entKey: string,
  styKey: string,
  fallback: number,
): number {
  const primary = Number(props[flagOn ? entKey : styKey]);
  if (Number.isFinite(primary)) return primary;
  const alt = Number(props[flagOn ? styKey : entKey]);
  return Number.isFinite(alt) ? alt : fallback;
}

function readMlStr(
  props: Record<string, string>,
  flagOn: boolean,
  entKey: string,
  styKey: string,
  fallback: string,
): string {
  const primary = props[flagOn ? entKey : styKey];
  if (primary && primary.length > 0) return primary;
  const alt = props[flagOn ? styKey : entKey];
  return alt && alt.length > 0 ? alt : fallback;
}

function readMlBool(
  props: Record<string, string>,
  flagOn: boolean,
  entKey: string,
  styKey: string,
  fallback: boolean,
): boolean {
  const primary = props[flagOn ? entKey : styKey];
  if (primary === "true") return true;
  if (primary === "false") return false;
  const alt = props[flagOn ? styKey : entKey];
  if (alt === "true") return true;
  if (alt === "false") return false;
  return fallback;
}

/** Decode the compact leader-hierarchy JSON into the shared walker's raw shape. */
function decodeLeaderRoots(roots: EncodedLeaderRoot[]): RawLeaderRoot[] {
  return roots.map((root) => ({
    nodes: (root?.n ?? []).map((node) => ({
      doglegVector: node?.d ? { x: node.d[0], y: node.d[1] } : null,
      lines: (node?.l ?? []).map((line) => ({
        vertices: line.v.map(([vx, vy]) => ({ x: vx, y: vy })),
        arrowheadBlock: line.a,
        // S9b — LeaderLine break quads `[startX, startY, endX, endY]` → the
        // shared `LeaderBreak` shape so the WebGL walker splits the stroke at
        // the same gap the SVG renderer already draws (Codex P2).
        breaks: (line.b ?? []).map(([sx, sy, ex, ey]) => ({
          start: { x: sx, y: sy },
          end: { x: ex, y: ey },
        })),
      })),
    })),
  }));
}

/* ── Public entry point ──────────────────────────────────────────── */

export interface MultileaderAdapterContext extends AdapterContext {
  /** Block definitions for resolving block-content via flattenBlockInsert. */
  blocks: Record<string, BlockDefinition>;
  /** Active annotation scale; null = non-annotative drawing. */
  annotationScale?: number | null;
}

/**
 * Multileader entity → `RenderableEntity` (with `kind: "multileader"`
 * geometry).
 *
 * The renderer reads `geometry.primitives` and feeds each through
 * line / arrow / text / block-flatten pipelines.
 */
export function multileaderToPrimitives(
  entity: EntityPreview,
  ctx: MultileaderAdapterContext,
): RenderableEntity {
  const props = entity.properties;
  const flags = Math.round(readNumber(props, "MLENT_PROPERTY_OVERRIDE_FLAGS"));

  // Annotative context resolution.
  let annotativeContexts: EncodedAnnotativeContext[] = [];
  try {
    annotativeContexts = JSON.parse(
      props.__mleader_annotative_contexts ?? "[]",
    );
  } catch {
    annotativeContexts = [];
  }
  const annoCtx =
    props.MLENT_ANNOTATIVE_ENABLED === "true"
      ? pickAnnotativeContext(annotativeContexts, ctx.annotationScale ?? null)
      : null;

  // Leader hierarchy.
  let encodedRoots: EncodedLeaderRoot[] = [];
  try {
    encodedRoots = JSON.parse(props.__mleader_leader_roots ?? "[]");
  } catch {
    encodedRoots = [];
  }

  // Block-content ATTRIB substitution data (`MultileaderEntity.
  // blockAttributeReferences[]`, encoded the SAME way as `leaderRoots`
  // above by `dxfGeometry.ts` under the `__mleader_block_attrs` key).
  let blockAttrRefs: EncodedBlockAttrRef[] = [];
  try {
    blockAttrRefs = JSON.parse(props.__mleader_block_attrs ?? "[]");
  } catch {
    blockAttrRefs = [];
  }

  // Resolved style values (with override flag + annotative ctx).
  const leaderType = annoCtx
    ? Math.round(readNumber(props, "MLENT_LEADER_TYPE", 1))
    : Math.round(
        readMlNum(
          props,
          (flags & FLAG_LEADER_TYPE) !== 0,
          "MLENT_LEADER_TYPE",
          "MLVAR_LEADER_TYPE",
          1,
        ),
      );
  const arrowheadBlock = readMlStr(
    props,
    (flags & FLAG_ARROWHEAD) !== 0,
    "MLENT_ARROWHEAD_BLOCK",
    "MLVAR_ARROWHEAD_BLOCK",
    "",
  );
  const arrowheadSize =
    annoCtx?.arrowheadSize ??
    readMlNum(
      props,
      (flags & FLAG_ARROWHEAD_SIZE) !== 0,
      "MLENT_ARROWHEAD_SIZE",
      "MLVAR_ARROWHEAD_SIZE",
      2.5,
    );
  const landingEnabled = readMlBool(
    props,
    (flags & FLAG_ENABLE_LANDING) !== 0,
    "MLENT_LANDING_ENABLED",
    "MLVAR_LANDING_ENABLED",
    true,
  );
  const doglegEnabled = readMlBool(
    props,
    (flags & FLAG_ENABLE_DOGLEG) !== 0,
    "MLENT_DOGLEG_ENABLED",
    "MLVAR_DOGLEG_ENABLED",
    true,
  );
  const doglegLength =
    annoCtx?.doglegLength ??
    readMlNum(
      props,
      (flags & FLAG_DOGLEG_LENGTH) !== 0,
      "MLENT_DOGLEG_LENGTH",
      "MLVAR_DOGLEG_LENGTH",
      8,
    );
  const landingGap =
    annoCtx?.landingGap ??
    readMlNum(
      props,
      (flags & FLAG_LANDING_GAP) !== 0,
      "MLENT_LANDING_GAP",
      "MLVAR_LANDING_GAP",
      2,
    );
  const contentType = Math.round(
    readMlNum(
      props,
      (flags & FLAG_CONTENT_TYPE) !== 0,
      "MLENT_CONTENT_TYPE",
      "MLVAR_CONTENT_TYPE",
      2,
    ),
  );
  const textHeight =
    annoCtx?.textHeight ??
    readMlNum(
      props,
      (flags & FLAG_TEXT_HEIGHT) !== 0,
      "MLENT_TEXT_HEIGHT",
      "MLVAR_TEXT_HEIGHT",
      2.5,
    );
  // GitHub Codex review PR #558 (post-rebase fresh pass) P1 — the bag key
  // (like the typed model field it mirrors verbatim, `dxfGeometry.ts`'s
  // `MLENT_TEXT_ROTATION: String(entity.textRotation)`) carries RADIANS;
  // the shared render pipeline this raw field feeds expects DEGREES — see
  // `multileaderFromEntity.ts`'s `raw.textRotation` doc for the full
  // evidence. Convert at this legacy adapter boundary too, for golden
  // parity with the typed path.
  const textRotationRad =
    annoCtx?.textRotation ?? readNumber(props, "MLENT_TEXT_ROTATION", 0);
  const textRotation = (textRotationRad * 180) / Math.PI;
  const textColor =
    annoCtx?.textColor ??
    readMlStr(
      props,
      (flags & FLAG_TEXT_COLOR) !== 0,
      "MLENT_TEXT_COLOR",
      "MLVAR_TEXT_COLOR",
      "ByLayer",
    );
  const blockContentName = readMlStr(
    props,
    (flags & FLAG_BLOCK_CONTENT_BLOCK) !== 0,
    "MLENT_BLOCK_NAME",
    "MLVAR_BLOCK_NAME",
    "",
  );
  // Codex S9b P2 — MLEADER block-content colour (group 93). Mirrors the SVG
  // twin's `readMlStringWithOverride(... MLENT_BLOCK_COLOR, MLVAR_BLOCK_COLOR)`
  // (override bit 1 << 20). Threaded into the block-content flatten so BYBLOCK
  // children inherit it. Default "ByBlock" matches the typed model default.
  const blockColor = readMlStr(
    props,
    (flags & FLAG_BLOCK_CONTENT_COLOR) !== 0,
    "MLENT_BLOCK_COLOR",
    "MLVAR_BLOCK_COLOR",
    "ByBlock",
  );
  const blockScale = readMlNum(
    props,
    (flags & FLAG_BLOCK_CONTENT_SCALE) !== 0,
    "MLENT_BLOCK_SCALE",
    "MLVAR_BLOCK_SCALE",
    1,
  );
  const blockRotation = readMlNum(
    props,
    (flags & FLAG_BLOCK_CONTENT_ROTATION) !== 0,
    "MLENT_BLOCK_ROTATION",
    "MLVAR_BLOCK_ROTATION",
    0,
  );

  const contents = props.MLENT_TEXT_CONTENTS || props.Contents || entity.label;

  const raw: MultileaderRawFields = {
    leaderRoots: decodeLeaderRoots(encodedRoots),
    leaderType,
    arrowheadBlockFallback: arrowheadBlock,
    arrowheadSize,
    landingEnabled,
    doglegEnabled,
    doglegLength,
    landingGap,
    contentType,
    textHeight,
    textRotation,
    // GitHub Codex review PR #558 (post-rebase fresh pass) P2 — the
    // `MLENT_TEXT_MIRRORED` bag key (`state/dxfGeometry.ts`'s
    // `buildMultileaderPreview`) so this LEGACY path (draft-preview channel
    // + PDF export converter) observes a MIRRTEXT=1-mirrored MLeader's
    // glyph handedness, matching the typed adapter.
    textMirrored: props.MLENT_TEXT_MIRRORED === "1",
    textColorRaw: textColor,
    blockColor,
    blockContentName,
    blockScale,
    blockRotation,
    textLocationRaw: {
      x: readNumber(props, "Landing X"),
      y: readNumber(props, "Landing Y"),
    },
    contents,
  };

  // Resolve color via shell builder so BYLAYER/BYBLOCK chain runs.
  const colorHex = resolveShellColorHex(entity, ctx);

  const result = buildMultileaderGeometry({
    raw,
    colorHex,
    // Codex round-6 P2 — string-bag resolves layer as
    // `properties.Layer ?? properties.layer ?? "0"` (mirrors
    // `buildRenderableShell`); threaded so layer-0 block children inherit the
    // MLEADER layer.
    layer: entity.properties.Layer ?? entity.properties.layer ?? "0",
    // Codex round-7 P2 — the MLEADER's OWN linetype token, threaded so a BYBLOCK
    // block-content child inherits the callout's linetype (see
    // `buildMultileaderGeometry`). Bag reads `Linetype` (typed key) with the
    // lowercase `linetype` fallback, mirroring `buildRenderableShell`'s group-6
    // read; absent → `resolveInsertLinetypeTyped` treats it as ByLayer.
    linetypeRaw: entity.properties.Linetype ?? entity.properties.linetype,
    attributes: mapBlockAttributeReferences(
      blockAttrRefs,
      // Codex PR #784 round 3 P2 — own-property-safe block-table read (a
      // MLEADER block-content name is raw DXF group-2 text).
      safeRecordLookup(ctx.blocks, blockContentName),
    ),
    blocks: ctx.blocks,
    ctx,
    spaceId: entity.spaceId,
    parentEntityId: entity.id,
    // Codex PR #784 round 4 F1 — the block-content flatten is INJECTED into
    // the shared builder (it no longer imports it; see
    // `adapters/typed/blockReferenceFlatten.ts`). A top-level MLEADER passes
    // the production entry verbatim, so this legacy bag path keeps flattening
    // exactly as before.
    flattenBlockContent: flattenBlockReference,
  });

  const geometry = toMultileaderSceneGeometry(result.primitives);
  return buildRenderableShell(entity, ctx, geometry, result.bbox);
}
